import * as Api from '@ViewModels';
import { IRange } from '@ViewModels';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { Waypoint } from 'react-waypoint';
import { NotesDateFilterOptionValue, NotesFilterOptionValue, NotesSortOptionValue } from '../../../../models';
import { useErrorMessages, useUserSession } from '../../../../models/hooks/appStateHooks';
import { Topics } from '../../../../models/LocalNotificationTopics';
import { useEventLogging } from '../../../../models/Logging';
import { getDisplayName } from '../../../../models/NameUtils';
import { formatDateCriteria, formatDateRangeString } from '../../../../models/UiUtils';
import {
	NotesSearchSortByType,
	invalidateNotesByAccount,
	invalidateNotesByMe,
	invalidateNotesSearch,
	useDeleteNoteMutation,
	useInfiniteNotesSearch,
} from '../../../../queries';
import { NoteCard } from '../../cards/NoteCard';
import { ConfirmationDialog, IConfirmationDialogOption } from '../../ConfirmationDialog';
import { DateRangePickerSideModal } from '../../DateRangePickerSide';
import { DeprecatedSelect, ISelectOption } from '../../DeprecatedSelect';
import { FabCalloutPlaceholder } from '../../FabCalloutPlaceholder';
import { LoadingSpinner } from '../../LoadingSpinner';
import { LocalNotificationObserver } from '../../LocalNotificationObserver';
import { Portal } from '../../Portal';
import { NotesPlaceholderIcon } from '../../svgs/icons/NotesPlaceholderIcon';
import { SearchIcon } from '../../svgs/icons/SearchIcon';
import { WarningIcon } from '../../svgs/icons/WarningIcon';
import { TextInput } from '../../TextInput';
import { UserSelectModal } from '../../UserSelectModal';
import { styleSheet } from './styles';

export const NoteOptions: ISelectOption<NotesFilterOptionValue>[] = [
	{
		dataContext: 'byAccount',
		id: 'note-option-show-all',
		text: 'Show all notes',
	},
	{
		dataContext: 'byMe',
		id: 'note-option-mine-only',
		text: 'Show my notes only',
	},
	{
		dataContext: 'ownedBy',
		id: 'notes-filter-option-owned-by',
		text: 'Show notes owned by...',
	},
];

const DefaultDateRangeText = 'Filter by Date';
export const NoteDateOptions: ISelectOption<NotesDateFilterOptionValue>[] = [
	{
		dataContext: 'date',
		id: 'note-option-mine-asc',
		text: DefaultDateRangeText,
	},
];
export const NoteSortOptions: ISelectOption<NotesSortOptionValue>[] = [
	{
		dataContext: 'LastModifiedDate-dsc',
		id: 'note-sort-option-last-modified-date-dsc',
		text: 'Last Modified date (newest first)',
	},
	{
		dataContext: 'LastModifiedDate-asc',
		id: 'note-sort-option-last-modified-date-asc',
		text: 'Last Modified date (oldest first)',
	},
	{
		dataContext: 'CreationDate-dsc',
		id: 'note-sort-option-creation-date-dsc',
		text: 'Creation date (newest first)',
	},
	{
		dataContext: 'CreationDate-asc',
		id: 'note-sort-option-creation-date-asc',
		text: 'Creation date (oldest first)',
	},
];

interface IProps {
	scrollToBottomPortalId?: string;
	onEditNote: (noteId: string) => void;
}

function _NotesList({ onEditNote, scrollToBottomPortalId }: IProps) {
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();
	const { logApiError, logEvent } = useEventLogging();
	const [searchQuery, setSearchQuery] = React.useState('');
	const [debouncedSearchQuery, setDebouncedSearchQuery] = React.useState(searchQuery);
	const [noteIdToDelete, setNoteIdToDelete] = React.useState<string | null>(null);
	const [selectedFilterOption, setSelectedFilterOption] = React.useState(NoteOptions[1]);
	const activeQueryType: 'search' | 'byMe' | 'byAccount' | 'ownedBy' = React.useMemo(() => {
		return debouncedSearchQuery ? 'search' : selectedFilterOption.dataContext;
	}, [selectedFilterOption, debouncedSearchQuery]);
	const [dateRange, setDateRange] = React.useState<IRange<Date | null>>({
		start: null,
		end: null,
	});
	const [showDateSelector, setShowDateSelector] = React.useState(false);
	const [selectedDateFilterOption, setSelectedDateFilterOption] = React.useState(NoteDateOptions[0]);
	const [selectedDateRangeText, setSelectedDateRangeText] = React.useState(DefaultDateRangeText);
	const [selectedSortFilterOption, setSelectedSortFilterOption] = React.useState(NoteSortOptions[0]);
	const [selectedSortText, setSelectedSortText] = React.useState(NoteSortOptions[0].text);
	const [showSelectEmployeeModal, setShowSelectEmployeeModal] = React.useState(false);
	const [assignedToUser, setAssignedToUser] = React.useState<Api.UserViewModel | undefined>();

	const filterRequest = React.useMemo(() => {
		const newFilterRequest: Api.IRichContentRequest = {
			criteria: [],
		};

		if (debouncedSearchQuery.length > 0) {
			newFilterRequest.criteria?.push({
				property: Api.RichContentProperty.Content,
				value: debouncedSearchQuery,
			});
		} else {
			newFilterRequest.criteria?.push({
				property: Api.RichContentProperty.All,
				value: 'All',
			});
		}
		if (dateRange?.start) {
			newFilterRequest.criteria?.push(
				formatDateCriteria<Api.RichContentProperty>(Api.RichContentProperty.CreationDate, dateRange)
			);
		}

		if (selectedFilterOption.dataContext === 'ownedBy') {
			newFilterRequest.criteria?.push({
				property: Api.RichContentProperty.OwnedBy,
				value: assignedToUser?.id,
			});
		}

		return newFilterRequest;
	}, [debouncedSearchQuery, dateRange, selectedFilterOption.dataContext, assignedToUser]);
	const sort = React.useMemo<Api.ISortDescriptor<NotesSearchSortByType>>(() => {
		let newSort: 'asc' | 'desc' = 'asc';
		let sortBy: NotesSearchSortByType = 'lastModifiedDate';
		switch (selectedSortFilterOption.dataContext) {
			case 'LastModifiedDate-asc':
				newSort = 'asc';
				sortBy = 'lastModifiedDate';
				break;
			case 'LastModifiedDate-dsc':
				newSort = 'desc';
				sortBy = 'lastModifiedDate';
				break;
			case 'CreationDate-asc':
				newSort = 'asc';
				sortBy = 'creationDate';
				break;
			case 'CreationDate-dsc':
				newSort = 'desc';
				sortBy = 'creationDate';
				break;
			default:
				break;
		}
		return { sort: newSort, sortBy };
	}, [selectedSortFilterOption]);

	const onRangeChangedCallback = (range: IRange<Date | null>) => {
		const nextDateRange = {
			start: range.start,
			end: range.end,
		};

		setDateRange(nextDateRange);
	};

	const onSelectAnEmployeeClose = (user?: Api.UserViewModel, cancelled?: boolean) => {
		if (cancelled) {
			setSelectedFilterOption(NoteOptions[1]);
		} else {
			setSelectedFilterOption(NoteOptions[2]);
			setAssignedToUser(user);
		}
		setShowSelectEmployeeModal(false);
	};

	const notesSearchQuery = useInfiniteNotesSearch({
		currentUserOnly: selectedFilterOption.dataContext === 'byMe',
		enabled: activeQueryType === 'search',
		query: debouncedSearchQuery,
		filterRequest,
		sort: sort.sort,
		sortBy: sort.sortBy,
	});

	const notesByMeQuery = useInfiniteNotesSearch({
		currentUserOnly: selectedFilterOption.dataContext === 'byMe',
		enabled: activeQueryType === 'byMe',
		query: debouncedSearchQuery,
		filterRequest,
		sort: sort.sort,
		sortBy: sort.sortBy,
	});

	const notesByAccountQuery = useInfiniteNotesSearch({
		currentUserOnly: selectedFilterOption.dataContext === 'byMe',
		enabled: activeQueryType === 'byAccount',
		query: debouncedSearchQuery,
		filterRequest,
		sort: sort.sort,
		sortBy: sort.sortBy,
	});

	const notesOwnedByQuery = useInfiniteNotesSearch({
		currentUserOnly: selectedFilterOption.dataContext === 'byMe',
		enabled: activeQueryType === 'ownedBy' && Boolean(assignedToUser),
		query: debouncedSearchQuery,
		filterRequest,
		sort: sort.sort,
		sortBy: sort.sortBy,
	});

	const handleInvalidateAllNotes = () => {
		invalidateNotesByAccount();
		invalidateNotesByMe();
		invalidateNotesSearch();
	};
	const deleteNoteMutation = useDeleteNoteMutation({
		onError: error => {
			logApiError('DeleteNote-Error', error);

			errorMessages.pushApiError(error);
		},
		onSuccess: handleInvalidateAllNotes,
	});
	React.useEffect(() => {
		const handler = setTimeout(() => {
			setDebouncedSearchQuery(searchQuery);
		}, 300);
		return () => {
			clearTimeout(handler);
		};
	}, [searchQuery]);
	const activeQuery = {
		byAccount: notesByAccountQuery,
		byMe: notesByMeQuery,
		search: notesSearchQuery,
		ownedBy: notesOwnedByQuery,
	}[activeQueryType];
	if (
		activeQueryType === 'byAccount' &&
		notesByAccountQuery.status === 'success' &&
		notesByAccountQuery.data.pages[0].values.length === 0
	) {
		return (
			<FabCalloutPlaceholder
				icon={<NotesPlaceholderIcon />}
				subtitle='Add your first note with the blue round button on the bottom right.'
				title='You don’t have any notes yet.'
			/>
		);
	}
	const onDeleteNoteConfirmationDialogRequestClose = (
		result?: IConfirmationDialogOption<boolean>,
		canceled?: boolean
	) => {
		if (result && !canceled && noteIdToDelete) {
			logEvent('DeleteNote', { id: noteIdToDelete });

			deleteNoteMutation.mutate({ id: noteIdToDelete });
		}
		setNoteIdToDelete(null);
	};

	const renderSelectTrigger = ({ dataContext, text }: ISelectOption<NotesFilterOptionValue>) => {
		return dataContext === 'ownedBy' ? (
			<div>Owned by {getDisplayName(assignedToUser) || '...'}</div>
		) : (
			<div>{text}</div>
		);
	};

	return (
		<>
			<div className={css(styleSheet.searchHeader)}>
				<div>
					<div className={css(styleSheet.searchHeaderWrap)}>
						<TextInput
							className={css(styleSheet.searchField)}
							inputId='notes-search'
							leftAccessory={<SearchIcon className={css(styleSheet.searchIcon)} />}
							onChange={ev => setSearchQuery(ev.target.value)}
							placeholder='Search notes'
							type='text'
							value={searchQuery}
						/>
						{debouncedSearchQuery.length > 0 ? (
							<button onClick={() => setSearchQuery('')} className={css(styleSheet.clear)}>
								Clear
							</button>
						) : null}
					</div>
				</div>
				<div className={css(styleSheet.searchContainer)}>
					<DeprecatedSelect
						options={NoteSortOptions}
						onOptionClick={option => {
							setSelectedSortFilterOption(option as ISelectOption<NotesSortOptionValue>);
							setSelectedSortText(option.text);
						}}
						selectedOption={{
							...selectedDateFilterOption,
							text: selectedSortText,
						}}
						styles={[styleSheet.sortSelect]}
					/>
					<DeprecatedSelect
						options={NoteDateOptions}
						onOptionClick={option => {
							setSelectedDateFilterOption(option);
							if (!dateRange?.start) {
								setDateRange({
									start: new Date(),
									end: new Date(),
								});
							}
							setShowDateSelector(true);
						}}
						selectedOption={{
							...selectedDateFilterOption,
							text: selectedDateRangeText,
						}}
						styles={[styleSheet.dateSelect]}
					/>
					<DateRangePickerSideModal
						isOpen={showDateSelector}
						onRequestClose={(clear = false) => {
							setShowDateSelector(false);
							if (clear) {
								setSelectedDateRangeText(DefaultDateRangeText);

								onRangeChangedCallback({ start: null, end: null });
							} else {
								setSelectedDateRangeText(formatDateRangeString(dateRange));
							}
						}}
						onRangeChange={onRangeChangedCallback}
						range={dateRange}
					/>
					<DeprecatedSelect
						styles={[styleSheet.filterSelect]}
						onOptionClick={option => {
							setSelectedFilterOption(option);
							if (option.dataContext === 'ownedBy') {
								setShowSelectEmployeeModal(true);
							}
						}}
						options={NoteOptions}
						optionStyles={[styleSheet.filterSelectOption]}
						selectedOption={selectedFilterOption}
						selectedOptionTitle={renderSelectTrigger}
					/>
					<UserSelectModal
						bodyText='Filter notes by those owned by an employee.'
						// className={`${css(styleSheet.selectAnEmployeeModal)}`}
						modalProps={{
							isOpen: showSelectEmployeeModal,
							onRequestClose: onSelectAnEmployeeClose,
							shouldCloseOnOverlayClick: true,
						}}
						title='Select an Employee'
					/>
				</div>
			</div>

			<div className={css(styleSheet.notesList)}>
				{activeQuery.isPending ? <LoadingSpinner type='large' /> : null}
				{activeQuery.status === 'success'
					? activeQuery.data.pages.map(group =>
							group.values.map(note => {
								const noteVm = new Api.NoteViewModel(userSession, note);
								return (
									<NoteCard
										className={css(styleSheet.note)}
										key={note.id}
										note={noteVm}
										onEditNote={onEditNote}
										onDeleteNote={id => setNoteIdToDelete(id)}
									/>
								);
							})
						)
					: null}
				{activeQuery.hasNextPage && !activeQuery.isFetchingNextPage ? (
					<Portal destination={scrollToBottomPortalId}>
						<Waypoint key='notes-list' bottomOffset='-200px' onEnter={() => activeQuery.fetchNextPage()} />
					</Portal>
				) : null}
				{activeQuery.isFetchingNextPage ? <LoadingSpinner type='small' /> : null}
			</div>
			<LocalNotificationObserver topic={Topics.CREATE_NOTE} onNotificationReceived={handleInvalidateAllNotes} />
			<ConfirmationDialog
				icon={<WarningIcon />}
				modalProps={{
					isOpen: !!noteIdToDelete,
					onRequestClose: onDeleteNoteConfirmationDialogRequestClose,
				}}
				options={[
					{
						isDestructive: true,
						representedObject: true,
						title: 'Delete',
					},
					{
						isCancel: true,
						representedObject: false,
						title: 'Cancel',
					},
				]}
				title='Are you sure you want to delete this note?'
			/>
		</>
	);
}

export const NotesList = observer(_NotesList);
